<script setup lang="ts">
import useVueformResponseErrors from 'vueform/src/VueformResponseErrors';
import {ref} from 'vue'
import type {Ref} from 'vue'
import type {JText} from 'joomla/joomla.interface';
import {type Vueform} from '@vueform/vueform';

const completed: Ref<boolean> = ref(false)
const form$: Ref<InstanceType<typeof Vueform> | null> = ref(null)
const nameLabel: Ref<string> = ref('')
const jText: JText = Joomla.JText

function success(): void {
  const isInPopup: boolean = Joomla.getOptions('configuration').renderInPopup;
  let formSuccessSelector: string = '.land-purchase-form .form-success';
  let introSelector: string = '.land-purchase-form .intro';
  if (isInPopup) {
    const popupSelector: string = '.form-popup '
    formSuccessSelector = popupSelector + formSuccessSelector;
    introSelector = popupSelector + introSelector;
  }
  document.querySelector<HTMLElement>(formSuccessSelector)!.style.display = 'block'
  document.querySelector<HTMLElement>(introSelector)!.style.display = 'none'
  completed.value = true
}

function updateEntityLabels(): void {
  if (form$.value) {
    const isLegalEntity: boolean = form$.value.el$('entity_type')?.value === 'business';
    nameLabel.value = isLegalEntity
        ? jText._('MOD_REACTIVE_FORMS_LVM_LAND_PURCHASE_LEGAL_ENTITY_NAME_LABEL')
        : jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_CONTACT_NAME');
  }
}
</script>
<template>
  <Vueform
      ref="form$"
      v-if="!completed"
      form-key="lvm-land-purchase"
      @success="success"
      @response="useVueformResponseErrors as any"
      :add-classes="{GroupElement:{container: 'group-element'}}"
  >
    <RadiogroupElement
        name="entity_type"
        view="tabs"
        default="private"
        :items="{
          'private': jText._('MOD_REACTIVE_FORMS_LVM_LAND_PURCHASE_INDIVIDUAL_ENTITY'),
          'business': jText._('MOD_REACTIVE_FORMS_LVM_LAND_PURCHASE_LEGAL_ENTITY')
        }"
        @change="updateEntityLabels"
        @mounted="updateEntityLabels"
    />
    <GroupElement name="owner" :label="jText._('MOD_REACTIVE_FORMS_LVM_LAND_PURCHASE_OWNER_GROUP_LABEL')">
      <TextElement
          name="contact_name"
          rules="required"
          :label="nameLabel"
      />
      <TextElement
          name="contact_email"
          rules="required|email:debounce=1000"
          :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_CONTACT_EMAIL')"
          :columns="{ default: 12, md: 6 }"
      />
      <TextElement
          name="contact_phone"
          rules="required|digits:8"
          input-type="number"
          :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_CONTACT_PHONE')"
          :debounce="1000"
          :add-class="{input: ['phone-number']}"
          :addons="{ before: '+371'}"
          :columns="{ default: 12, md: {container: 6, wrapper: 6}}"
      />
    </GroupElement>
    <GroupElement name="land" :label="jText._('MOD_REACTIVE_FORMS_LVM_LAND_PURCHASE_LAND_GROUP_LABEL')">
      <TextElement
          name="land_name"
          rules="required"
          :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_LAND_NAME')"
          :columns="{ default: 12, md: 8 }"
      />
      <TextElement
          name="land_size"
          rules="required"
          :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_LAND_SIZE')"
          :columns="{ default: 12, md: 4}"
      />
      <TextElement
          name="land_address"
          rules="required"
          :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_LAND_ADDRESS')"
      />
    </GroupElement>
    <GroupElement name="attachments" :label="jText._('MOD_REACTIVE_FORMS_LVM_LAND_PURCHASE_ATTACHMENTS_GROUP_LABEL')">
      <MultifileElement
          name="files"
          rules="max:5"
          :label="jText._('MOD_REACTIVE_FORMS_VALIDATION_ATTRIBUTE_FILES')"
          :info="jText._('MOD_REACTIVE_FORMS_LVM_LAND_PURCHASE_ATTACHMENTS_DESCRIPTION')"
          :sort="false"
          :controls="{ sort: false }"
          :drop="true"
          :upload-temp-endpoint="false"
          :file="{
            rules: 'file|mimes:pdf,doc,docx,jpg,png|max:10240',
            messages: {
              mimes: jText._('MOD_REACTIVE_FORMS_LVM_LAND_PURCHASE_INCORRECT_ATTACHMENT_MIME'),
              max: jText._('MOD_REACTIVE_FORMS_LVM_LAND_PURCHASE_ATTACHMENT_TOO_LARGE')
            }
          }"
      />
    </GroupElement>
    <CaptchaElement name="g_recaptcha_response"/>
    <ButtonElement
        name="submit"
        align="left"
        :submits="true"
        :button-label="jText._('MOD_REACTIVE_FORMS_LVM_LAND_PURCHASE_SUBMIT_BUTTON')"
        :add-class="{button: ['btn']}"
    />
  </Vueform>
</template>
